import { Selector } from '@ngxs/store';
import { BusinessChecklistsState } from './checklists.state';
import { BusinessChecklistsModel, SortOption } from './checklists.types';
import {
	BusinessChecklistVM,
	ChecklistItem,
	ChecklistStageVM,
	ChecklistTaskVM,
	PurchaseChecklistItems,
	RefinanceChecklistItems,
	SaleChecklistItems,
	SampleChecklistTemplates,
} from '@models/checklist';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterFileType } from '@models/matter';
import { getCongratsMessage } from '@core/util/string';
import { sortItems } from '@core/util/checklist';
import { UserProfileState } from '@state/user-profile';

export class BusinessChecklistsSelectors {
	@Selector([BusinessChecklistsState, UserProfileState.getCurrentBusinessId])
	static getAll(
		state: BusinessChecklistsModel,
		businessId: string,
	): ChecklistItem[] {
		const SampleChecklists: ChecklistItem[] = [
			...SampleChecklistTemplates.map((item) => ({
				...item,
				businessId,
				default: !state.data.find(
					(template) => template.fileType === item.fileType && template.default,
				),
			})),
			...SaleChecklistItems.map((item) => ({ ...item, businessId })),
			...PurchaseChecklistItems.map((item) => ({ ...item, businessId })),
			...RefinanceChecklistItems.map((item) => ({ ...item, businessId })),
		];

		return [...SampleChecklists, ...(state?.data || [])];
	}

	private static getTasks(
		items: ChecklistItem[],
		parentId: string,
	): ChecklistTaskVM[] {
		const filteredTasks = items?.filter(
			(_item) => _item.type === 'task' && _item.parentId === parentId,
		);
		const sortedTasks = sortItems(filteredTasks);
		const tasks: ChecklistTaskVM[] = sortedTasks.map((item) => {
			const task: ChecklistTaskVM = {
				id: item.id,
				title: item.title,
				complete: item.complete,
				fieldPath: item.fieldPath,
			};
			return task;
		});
		return tasks;
	}

	private static getStages(
		items: ChecklistItem[],
		parentId: string,
	): ChecklistStageVM[] {
		const filteredStages = items?.filter(
			(_item) => _item.type === 'stage' && _item.parentId === parentId,
		);
		const sortedStages = sortItems(filteredStages);
		const stages: ChecklistStageVM[] = sortedStages.map((item) => {
			const stage: ChecklistStageVM = {
				id: item.id,
				title: item.title,
				tasks: this.getTasks(items, item.id),
			};
			return stage;
		});
		return stages;
	}

	@Selector([BusinessChecklistsSelectors.getAll])
	static getBusinessChecklists(items: ChecklistItem[]): BusinessChecklistVM[] {
		const templates: BusinessChecklistVM[] = items
			?.filter((_item) => _item.type === 'template')
			.map((item) => {
				const template: BusinessChecklistVM = {
					id: item.id,
					title: item.title,
					stages: this.getStages(items, item.id),
					congratsMessage: item.congratsMessage,
					draft: item.draft,
					fileType: item.fileType,
					lastEdited: item.lastEdited,
					created: item.created,
					locked: item.locked,
					default: item.default,
				};
				return template;
			});

		return templates;
	}

	@Selector([
		BusinessChecklistsSelectors.getBusinessChecklists,
		SelectedMatter.matterFileType,
	])
	static getChecklistsByFileType(
		items: BusinessChecklistVM[],
		fileType: MatterFileType,
	): BusinessChecklistVM[] {
		if (!fileType || !items || !items.length) {
			return [];
		}
		return items?.filter(
			(checklist) => checklist.fileType === fileType && !checklist.draft,
		);
	}

	@Selector([BusinessChecklistsSelectors.getAll])
	static getItemsByTemplateId(
		_items: ChecklistItem[],
	): (id: string) => ChecklistItem[] {
		return (id: string): ChecklistItem[] => {
			const items = structuredClone(_items);

			// Find the template
			const template: ChecklistItem = {
				...items.find((item) => item.id === id),
				congratsMessage: getCongratsMessage(),
			};

			// Find all stages that belong to the template
			const stages = items.filter((item) => item.parentId === id);

			// Open the first stage by default
			const firstStage = stages.find(
				(item) => item.type === 'stage' && item.headNode === true,
			);
			firstStage.opened = true;

			// Find all tasks that belong to any of the stages
			const tasks = items.filter((item) =>
				stages.some((stage) => stage.id === item.parentId),
			);

			// Combine template, stages, and tasks
			return [template, ...stages, ...tasks].filter(Boolean);
		};
	}

	@Selector([BusinessChecklistsSelectors.getAll])
	static customTemplateExists(items: ChecklistItem[]): boolean {
		return items?.some((template) => !template.locked);
	}

	@Selector([BusinessChecklistsSelectors.getAll])
	static getDefaultChecklistItemsSale(
		_items: ChecklistItem[],
	): ChecklistItem[] {
		const items = structuredClone(_items);

		const template: ChecklistItem = {
			...items.find(
				(item) => item.fileType === MatterFileType.SALE && item.default,
			),
			congratsMessage: getCongratsMessage(),
		};
		const stages = items.filter((item) => item.parentId === template.id);

		// Open the first stage by default
		const firstStage = stages.find(
			(item) => item.type === 'stage' && item.headNode === true,
		);
		firstStage.opened = true;

		const tasks = items.filter((item) =>
			stages.some((stage) => stage.id === item.parentId),
		);

		return [template, ...stages, ...tasks].filter(Boolean);
	}

	@Selector([BusinessChecklistsSelectors.getAll])
	static getDefaultChecklistItemsPurchase(
		_items: ChecklistItem[],
	): ChecklistItem[] {
		const items = structuredClone(_items);

		const template: ChecklistItem = {
			...items.find(
				(item) => item.fileType === MatterFileType.PURCHASE && item.default,
			),
			congratsMessage: getCongratsMessage(),
		};
		const stages = items.filter((item) => item.parentId === template.id);

		// Open the first stage by default
		const firstStage = stages.find(
			(item) => item.type === 'stage' && item.headNode === true,
		);
		firstStage.opened = true;

		const tasks = items.filter((item) =>
			stages.some((stage) => stage.id === item.parentId),
		);

		return [template, ...stages, ...tasks].filter(Boolean);
	}

	@Selector([BusinessChecklistsSelectors.getAll])
	static getDefaultChecklistItemsRefinance(
		_items: ChecklistItem[],
	): ChecklistItem[] {
		const items = structuredClone(_items);

		const template: ChecklistItem = {
			...items.find(
				(item) => item.fileType === MatterFileType.REFINANCE && item.default,
			),
			congratsMessage: getCongratsMessage(),
		};
		const stages = items.filter((item) => item.parentId === template.id);

		// Open the first stage by default
		const firstStage = stages.find(
			(item) => item.type === 'stage' && item.headNode === true,
		);
		firstStage.opened = true;

		const tasks = items.filter((item) =>
			stages.some((stage) => stage.id === item.parentId),
		);

		return [template, ...stages, ...tasks].filter(Boolean);
	}

	@Selector([BusinessChecklistsSelectors.getBusinessChecklists])
	static getDefaultChecklistSaleVM(
		items: BusinessChecklistVM[],
	): BusinessChecklistVM {
		return items.find(
			(item) => item.fileType === MatterFileType.SALE && item.default,
		);
	}

	@Selector([BusinessChecklistsSelectors.getBusinessChecklists])
	static getDefaultChecklistPurchaseVM(
		items: BusinessChecklistVM[],
	): BusinessChecklistVM {
		return items.find(
			(item) => item.fileType === MatterFileType.PURCHASE && item.default,
		);
	}

	@Selector([BusinessChecklistsSelectors.getBusinessChecklists])
	static getDefaultChecklistRefinanceVM(
		items: BusinessChecklistVM[],
	): BusinessChecklistVM {
		return items.find(
			(item) => item.fileType === MatterFileType.REFINANCE && item.default,
		);
	}

	@Selector([BusinessChecklistsState])
	static getSortOption(state: BusinessChecklistsModel): SortOption {
		return state.sortOption;
	}

	@Selector([BusinessChecklistsState])
	static getSelectedFileType(
		state: BusinessChecklistsModel,
	): MatterFileType | 'all' {
		return state.selectedFileType;
	}

	private static sortChecklists(
		checklists: BusinessChecklistVM[],
		sortOption: SortOption,
	): BusinessChecklistVM[] {
		switch (sortOption) {
			case SortOption.RECENTLY_CREATED:
				return checklists.sort((a, b) => {
					if (a.locked && !b.locked) {
						return -1;
					} else if (!a.locked && b.locked) {
						return 1;
					} else {
						return (
							new Date(a.lastEdited).getTime() -
							new Date(b.lastEdited).getTime()
						);
					}
				});
			case SortOption.A_TO_Z:
				return checklists.sort((a, b) => a.title.localeCompare(b.title));
			case SortOption.DRAFT:
				return checklists.sort((a) => (a.draft ? 1 : -1));
			case SortOption.FIRM_CREATED:
				return checklists.sort((a) => (a.locked ? 1 : -1));
			case SortOption.CLOSER_CREATED:
				return checklists.sort((a) => (a.locked ? -1 : 1));
			default:
				return checklists;
		}
	}

	private static filterChecklists(
		checklists: BusinessChecklistVM[],
		fileType: MatterFileType | 'all',
	): BusinessChecklistVM[] {
		if (fileType === 'all') {
			return checklists;
		}

		return checklists.filter((checklist) => checklist.fileType === fileType);
	}

	@Selector([
		BusinessChecklistsSelectors.getBusinessChecklists,
		BusinessChecklistsSelectors.getSortOption,
		BusinessChecklistsSelectors.getSelectedFileType,
	])
	static getSortedChecklists(
		businessChecklists: BusinessChecklistVM[],
		selectedSortOption: SortOption,
		selectedFileType: MatterFileType | 'all',
	): BusinessChecklistVM[] {
		return this.filterChecklists(
			this.sortChecklists(businessChecklists, selectedSortOption),
			selectedFileType,
		);
	}
}
